import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '@/lib/config';
import { useLang } from '@/i18n/LanguageContext';
import { Container, SectionKicker, TricolourRibbon } from '@/components/common/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';

export default function NewsPage() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/news`);
        if (!c) setItems(res.data.items || []);
      } catch (err) {
        void err;
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => { c = true; };
  }, []);

  const categories = useMemo(() => {
    const s = new Set(items.map((i) => i.category));
    return ['all', ...Array.from(s)];
  }, [items]);

  const filtered = category === 'all' ? items : items.filter((i) => i.category === category);

  return (
    <div>
      <section className="hero-surface">
        <Container className="py-14 sm:py-16">
          <SectionKicker>{t('nav.news')}</SectionKicker>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220]">
            {t('news.title')}
          </h1>
          <p className="mt-4 max-w-3xl text-[#5B6B82] leading-relaxed">{t('news.lede')}</p>

          <div className="mt-6 w-full max-w-xs">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="news-category-filter" className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c === 'all' ? t('news.filter_all') : c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Container>
      </section>

      <section className="py-14">
        <Container>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-32 w-full" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {filtered.map((it) => (
                <Card key={it.id} className="border-[#D7E1EF] bg-white" data-testid="news-item-card">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-[#D7E1EF] text-[#062B5C]">{it.category}</Badge>
                      <span className="text-xs text-[#5B6B82]">{it.date}</span>
                    </div>
                    <div className="mt-3 font-semibold text-[#0B1220]">{it.title}</div>
                    <div className="mt-2 text-sm text-[#5B6B82] leading-relaxed">{it.summary}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </Container>
      </section>

      <TricolourRibbon />
    </div>
  );
}
